<template>
  <div>
    <v-btn id="key_export_back_btn" icon @click="closeModal">
      <v-icon>chevron_left</v-icon>
    </v-btn>

    <v-row no-gutters align="center">
      <span class="text-h6 context_title_color--text">
        {{ $tc('export') }}
      </span>

      <span class="text-h5 font-weight-medium mx-1">
        {{ '/' }}
      </span>

      <svg
        class="mr-2"
        style="width: 24px; height: 24px"
        viewBox="0 0 24 24"
      >
        <path :fill="iconcolor" :d="svg" />
      </svg>

      <card-toolbar :title="thename" class="mt-md-2" />
    </v-row>

    <v-row no-gutters align="center">
      <v-col cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('type') }}
        </span>

        <p>
          {{ keyTypeId }}
        </p>
      </v-col>

      <v-col v-if="kinfo" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('bchain_version') }}
        </span>

        <p>
          {{ this.$tc('yes') }}
        </p>

        <!-- <p>
          {{ kinfo.version === 1 ? this.$tc('yes') : this.$tc('no') }}
        </p> -->
      </v-col>

      <v-col v-if="kinfo && kinfo?.version === 1" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_depth') }}
        </span>

        <p>
          {{ kinfo.x_depth }}
        </p>
      </v-col>

      <v-col v-if="kinfo && kinfo?.version === 1" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_ver') }}
        </span>

        <p>
          {{ versionInfo(kinfo.x_ver) }}
        </p>
      </v-col>

      <v-col v-if="kinfo && kinfo?.version === 1" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_par_fp') }}
        </span>

        <p>
          {{ kinfo.x_par_fp }}
        </p>
      </v-col>

      <v-col v-if="kinfo && kinfo?.version === 1" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_child_num') }}
        </span>

        <p>
          <span>{{ parseInt(kinfo.x_child_num, 16) }}</span>
          <span>{{ '/' }}</span>
          <span class="subtitle-2 context_title_color--text">{{ '0x' + kinfo.x_child_num }}</span>
        </p>
      </v-col>
    </v-row>

    <!-- FIRST OPTION -->
    <template v-if="!needsImmediateExport">
      <v-select
        id="KeysExportModal_firstOption"
        v-model="firstOption"
        :items="keysFirstOptions"
        item-text="name"
        item-value="value"
        :label="$t('choose_export_method')"
        :error-messages="firstOptionErrors"
        :readonly="publicKeyExportAction"
        @input="onFirstOptionSelected()"
        @blur="$v.firstOption.$touch()"
      />
    </template>

    <!-- SECOND OPTION -->
    <template v-if="keysSecondOptions">
      <v-select
        id="KeysExportModal_secondOption"
        v-model="secondOption"
        :items="keysSecondOptions"
        item-text="name"
        item-value="value"
        :label="$tc('select_option')"
        :error-messages="secondOptionErrors"
        @input="onSecondOptionSelected"
        @blur="$v.secondOption.$touch()"
      />
    </template>

    <!-- KEK -->
    <template v-if="firstOption === 'With KEK'">
      <!-- KEK SELECTION -->
      <v-autocomplete
        id="KeysExportModal_kek"
        v-model="kek"
        :items="keys"
        :loading="map.slot1.searching"
        :search-input.sync="map.slot1.search"
        hide-no-data
        hide-selected
        item-text="name"
        item-value="name"
        label="Key Encryption Key"
        :placeholder="$t('select_key')"
        :error-messages="kekErrors"
        @input="onKEKSelected()"
        @blur="$v.kek.$touch()"
      />

      <template v-if="needsMode">
        <transition name="fade">
          <v-select
            id="KeysExportModal_modeValue"
            v-model="modeValue"
            :items="modeOptions"
            item-text="name"
            item-value="value"
            :label="$t('mode')"
            :error-messages="modeValueErrors"
            @input="onModeSelected()"
            @blur="$v.modeValue.$touch()"
          />
        </transition>
      </template>

      <template v-if="needsPadding">
        <transition name="fade">
          <v-select
            id="KeysExportModal_paddingValue"
            v-model="paddingValue"
            :items="paddingOptions"
            item-text="name"
            item-value="value"
            :label="$t('padding')"
            :error-messages="paddingValueErrors"
            @input="$v.paddingValue.$touch()"
            @blur="$v.paddingValue.$touch()"
          />
        </transition>
      </template>

      <template v-if="needsIV">
        <transition name="fade">
          <v-text-field
            id="KeysExportModal_ivValue"
            :label="$tc('iv')"
            v-model="ivValue"
            :placeholder="'IV (' + ivSize + ' Nibbles)'"
            :counter="ivSize"
            :error-messages="ivValueErrors"
            @input="$v.ivValue.$touch()"
            @blur="$v.ivValue.$touch()"
          />
        </transition>
      </template>
    </template>

    <!-- PKCS#12 & PKCS#8 -->
    <template v-if="isPKCS8or12">
      <template v-if="firstOption === 'PKCS#12'">
        <template v-if="keyType === 'x.509'">
          <v-text-field
            id="KeysExportModal_pkcs12PrivateKey"
            :label="$tc('private_key_name')"
            v-model="pkcs12PrivateKey"
            readonly
          />
        </template>
        <template v-else>
          <v-autocomplete
            id="KeysExportModal_pkcs12Certificate"
            v-model="pkcs12Certificate"
            :items="keys"
            :loading="map.slot1.searching"
            :search-input.sync="map.slot1.search"
            hide-no-data
            hide-selected
            item-text="name"
            item-value="name"
            :placeholder="$t('select_certificate')"
            :error-messages="pkcs12CertificateErrors"
            @input="$v.pkcs12Certificate.$touch()"
            @blur="$v.pkcs12Certificate.$touch()"
          />
        </template>
      </template>

      <v-text-field
        id="KeysExportModal_password"
        :label="$tc('password_optional')"
        v-model="password"
        counter
        type="password"
        autocomplete="new-password"
        :error-messages="passwordErrors"
        @input="$v.password.$touch()"
        @blur="$v.password.$touch()"
      />

      <v-text-field
        id="KeysExportModal_repeatPassword"
        :label="$tc('repeat_password')"
        v-model="repeatPassword"
        counter
        type="password"
        autocomplete="new-password"
        :error-messages="repeatPasswordErrors"
        @input="$v.repeatPassword.$touch()"
        @blur="$v.repeatPassword.$touch()"
      />
    </template>

    <!-- EMV CSR -->
    <template v-if="firstOption === 'EMV CSR'">
      <strong>{{ $tc("key_type") + ": " }}</strong>
      <p class="item.class">
        {{ this.keyType + this.keySize + this.keySubtype }}
      </p>

      <strong>{{ $tc("public_exponent") + ": " }}</strong>
      <p class="item.class">
        {{ this.pubExponent }}
      </p>

      <transition name="fade">
        <v-select
          id="KeysExportModal_crsType"
          v-model="crsType"
          :items="csrTypeOptions"
          item-text="name"
          item-value="value"
          :label="$t('csr_type')"
          :error-messages="crsTypeErrors"
          @input="$v.crsType.$touch()"
          @blur="$v.crsType.$touch()"
        />
      </transition>

      <v-text-field
        id="KeysExportModal_trackingNumber"
        :label="$tc('tracking_number')"
        v-model="trackingNumber"
        counter
        :error-messages="trackingNumberErrors"
        @input="$v.trackingNumber.$touch()"
        @blur="$v.trackingNumber.$touch()"
      />

      <v-text-field
        id="KeysExportModal_serviceId"
        :label="$tc('service_id')"
        v-model="serviceId"
        counter
        :error-messages="serviceIdErrors"
        @input="$v.serviceId.$touch()"
        @blur="$v.serviceId.$touch()"
      />

      <v-text-field
        id="KeysExportModal_issuerId"
        :label="$tc('issuer_id')"
        v-model="issuerId"
        counter
        :error-messages="issuerIdErrors"
        @input="$v.issuerId.$touch()"
        @blur="$v.issuerId.$touch()"
      />

      <v-text-field
        id="KeysExportModal_certificateDate"
        :label="$tc('certificate_expiry_date')"
        v-model="certificateDate"
        counter
        :error-messages="certificateDateErrors"
        @input="$v.certificateDate.$touch()"
        @blur="$v.certificateDate.$touch()"
      />
    </template>

    <!-- KEY COMPONENTS -->
    <template v-if="firstOption === 'Key Components'">
      <v-stepper
        id="export_key_components"
        v-model="currentStep"
        vertical
        elevation="0"
        style="background: transparent;"
      >
        <div v-if="isAES === true">
          <v-stepper-step :complete="currentStep > 1" step="0">
            {{ $t('select_type') }}
          </v-stepper-step>

          <v-stepper-content step="0">
            <v-radio-group class="step-content-indentation" row v-model="ckst">
              <v-radio label="KCV" value="1" />
              <v-radio label="CMAC" value="2" />
            </v-radio-group>

            <v-btn @click="aesExportKeyComponents" dark color="green">
              {{ $tc("next") }}
            </v-btn>
          </v-stepper-content>
        </div>

        <div v-else>
          <v-stepper-step :complete="currentStep > 1" step="0">
            {{ $tc("export_with_key_components") }}
          </v-stepper-step>

          <v-stepper-content step="0">
            <v-spacer />
            <v-alert
              border="left"
              colored-border
              type="warning"
            >
              {{ $tc("export_key_component_warning") }}
            </v-alert>
            <v-switch
              id="KeysExportModal_jcbValue"
              class="ml-2"
              color="green"
              :label="$t('use_jcb_format')"
              v-model="jcbValue"
            />
            <v-btn @click="exportKeyComponents" dark color="blue">
              {{ $tc("next") }}
            </v-btn>
          </v-stepper-content>
        </div>

        <v-stepper-step :complete="currentStep > 1" step="1">
          {{ $tc("part") + " " + 1 }}
        </v-stepper-step>

        <v-stepper-content step="1">
          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_outBlock1"
                class="monospace"
                auto-grow
                row="1"
                row-height="10"
                label="Out Block"
                readonly
                :value="outBlock1"
              />
            </v-col>
            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    color="contrasty"
                    id="KeysExportModal_outBlock1_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="outBlock1"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_KCV1"
                class="monospace"
                auto-grow
                row="1"
                row-height="10"
                label="Key check Value"
                readonly
                :value="KCV1"
              />
            </v-col>
            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_KCV1_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="KCV1"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-btn
            id="KeysExportModal_STEP 1"
            class="mb-2"
            color="blue"
            dark
            @click="currentStep = 1.1"
          >
            {{ $tc("next") }}
          </v-btn>
        </v-stepper-content>

        <!-- STEP 1.1 -->
        <v-stepper-step :complete="currentStep > 1.1" step="1.1">
          {{ $tc("part") + " " + 1.1 }}
        </v-stepper-step>

        <v-stepper-content step="1.1">
          <v-alert
            border="left"
            colored-border
            type="warning"
          >
            {{
              this.$t("next_component_warning", {
                num: 2,
              })
            }}
          </v-alert>

          <v-btn
            id="KeysExportModal_STEP 1.1"
            class="mb-2"
            color="blue"
            dark
            @click="currentStep = 2"
          >
            {{ $tc("next") }}
          </v-btn>
        </v-stepper-content>

        <!-- STEP 2 -->
        <v-stepper-step :complete="currentStep > 2" step="2">
          {{ $tc("part") + " " + 2 }}
        </v-stepper-step>

        <v-stepper-content step="2">
          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_outBlock2"
                auto-grow
                row="1"
                row-height="10"
                label="Out Block"
                readonly
                :value="outBlock2"
              />
            </v-col>

            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_outBlock2_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="outBlock2"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_KCV2"
                class="monospace"
                auto-grow
                row="1"
                row-height="10"
                label="Key check Value"
                readonly
                :value="KCV2"
              />
            </v-col>

            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_KCV2_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="KCV2"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-btn
            id="KeysExportModal_STEP 2"
            class="mb-2"
            color="blue"
            dark
            @click="currentStep = 2.1"
          >
            {{ $tc("next") }}
          </v-btn>
        </v-stepper-content>

        <!-- STEP 2.1 -->
        <v-stepper-step :complete="currentStep > 2.1" step="2.1">
          {{ $tc("part") + " " + 2.1 }}
        </v-stepper-step>

        <v-stepper-content step="2.1">
          <v-alert
            border="left"
            colored-border
            type="warning"
          >
            {{
              this.$t("next_component_warning", {
                num: 3,
              })
            }}
          </v-alert>

          <v-btn
            id="KeysExportModal_STEP 2.1"
            class="mb-2"
            color="blue"
            dark
            @click="currentStep = 3"
          >
            {{ $tc("next") }}
          </v-btn>
        </v-stepper-content>

        <!-- STEP 3 -->
        <v-stepper-step :complete="currentStep > 3" step="3">
          {{ $tc("part") + " " + 3 }}
        </v-stepper-step>

        <v-stepper-content step="3">
          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_outBlock3"
                class="monospace"
                auto-grow
                row="1"
                row-height="10"
                label="Out Block"
                readonly
                :value="outBlock3"
              />
            </v-col>

            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_outBlock3_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="outBlock3"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_KCV3"
                class="monospace"
                auto-grow
                row="1"
                row-height="10"
                label="Key check Value"
                readonly
                :value="KCV3"
              />
            </v-col>

            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_KCV3_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="KCV3"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-btn
            id="KeysExportModal_STEP 3"
            class="mb-2"
            color="blue"
            dark
            @click="currentStep = 4"
          >
            {{ $tc("next") }}
          </v-btn>
        </v-stepper-content>

        <!-- STEP 4 -->
        <v-stepper-step :complete="currentStep > 4" step="4">
          {{ "KCV final" }}
        </v-stepper-step>

        <v-stepper-content step="4">
          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                id="KeysExportModal_KCVfinal"
                class="monospace"
                auto-grow
                row="1"
                row-height="10"
                label="Key check Value"
                readonly
                :value="KCVfinal"
              />
            </v-col>

            <v-col cols="1">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_KCVfinal_clipboard"
                    icon
                    v-on="on"
                    v-clipboard:copy="KCVfinal"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-btn
            id="KeysExportModal_FinishComponentsExport"
            color="blue"
            dark
            @click="closeModal"
          >
            {{ $tc("ok") }}
          </v-btn>
        </v-stepper-content>
      </v-stepper>
    </template>

    <!-- OUTBLOCK & KCV & EXPORT BTN -->
    <template v-if="outBlockReady">
      <v-row no-gutters>
        <v-col cols="11">
          <v-textarea
            id="KeysExportModal_outBlock"
            class="monospace"
            auto-grow
            row="6"
            row-height="25"
            label="Out Block"
            readonly
            v-model="outBlock"
            :hint="materialFormatHint"
            persistent-hint
          />
        </v-col>
        <v-col cols="1">
          <v-row no-gutters>
            <v-col cols="12">
              <!-- BASE64 -->
              <template v-if="showConversionButton">
                <v-tooltip left>
                  <template #activator="{ on }">
                    <v-btn
                      id="KeysExportModal_outBlock_format"
                      icon
                      v-on="on"
                      class="my-1"
                      color="contrasty"
                      @click="changeMaterialFormat"
                    >
                      <v-icon>check_circle_outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ base64TooltipText }}</span>
                </v-tooltip>
              </template>
            </v-col>
            <v-col cols="12">
              <!-- CLIPBOARD -->
              <v-tooltip left>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_outBlock_clipboard"
                    icon
                    v-on="on"
                    class="my-1"
                    color="contrasty"
                    v-clipboard:copy="outBlock"
                  >
                    <v-icon>content_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("copy") }}</span>
              </v-tooltip>
            </v-col>
            <v-col cols="12">
              <!-- SAVE -->
              <v-tooltip left>
                <template #activator="{ on }">
                  <v-btn
                    id="KeysExportModal_outBlock_saveFile"
                    icon
                    v-on="on"
                    class="my-1"
                    color="contrasty"
                    @click="saveToFile(rawOutBlock)"
                  >
                    <v-icon>save</v-icon>
                  </v-btn>
                </template>
                <span>{{ $tc("save_to_file") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>

    <!-- KCV -->
    <template v-if="kcvReady">
      <v-row>
        <v-col cols="11">
          <v-textarea
            id="KeysExportModal_keyCheckValue"
            class="monospace"
            auto-grow
            row="1"
            row-height="15"
            readonly
            label="KCV"
            :value="keyCheckValue"
          />
        </v-col>
        <v-col cols="1">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn
                id="KeysExportModal_keyCheckValue_clipboard"
                icon
                v-on="on"
                v-clipboard:copy="keyCheckValue"
              >
                <v-icon>content_copy</v-icon>
              </v-btn>
            </template>
            <span>{{ $tc("copy") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn
                id="KeysExportModal_keyCheckValue_saveFile"
                icon
                v-on="on"
                @click="saveToFile(keyCheckValue)"
              >
                <v-icon>save</v-icon>
              </v-btn>
            </template>
            <span>{{ $tc("save_to_file") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </template>

    <template v-if="firstOption === 'Blockchain Export'">
      <export-blockchain :key-name="thename" :key-info="info" :key-option="keyImportOption" />
    </template>

    <template v-if="showExportButton">
      <v-row justify="end" class="mx-4 mb-4 mt-2">
        <v-btn
          id="KeysExportModal_export_btn"
          color="blue"
          :dark="!$v.$invalid"
          :disabled="$v.$invalid"
          @click="doExport"
        >
          {{ $t("export") }}
        </v-btn>
      </v-row>
    </template>
  </div>
</template>

<script>
import CardToolbar from '@/components/CardToolbar'
import { required, sameAs, minLength, maxLength } from 'vuelidate/lib/validators'
import { toBase64, arrayBufferToString } from 'pvutils'
import CryptoJS from 'crypto-js'
import ObjectUIMixin from '@/mixins/objectUI'
import Base62Str from 'base62str'

import ExportBlockchain from './ExportBlockchain.vue'
const aesCmac = require('node-aes-cmac').aesCmac

const base62 = Base62Str.createInstance()

const BITMASK_RSA_PUB_EXP3 = 1 << 12

const ALG_OBJ_BLOB_X509 = 13

const KEK_WRAP_NULL_PAD = 0
const KEK_WRAP_P5_PAD = 1
const KEK_WRAP_0_PAD = 2
const KEK_WRAP_OAEP_PAD = 3
const KEK_WRAP_P1_PAD = 4
const KEK_WRAP_OAEP2_PAD = 5

const MODE_NULL = 0
const MODE_ECB = 1
const MODE_CBC = 2

const XPRIV_TEST_NET = '04358394'
const XPRIV_MAIN_NET = '0488ADE4'

const FPRIV = '03273A10'
const FPUB = '0488b21e'

const HATHOR_MAIN_NET = '03523b05'
const HATHOR_TEST_NET = '0434c8c4'

const BASE64_FORMAT_VALUE = 1
const HEX_FORMAT_VALUE = 2
const BASE62_FORMAT_VALUE = 3

export default {
  mixins: [ObjectUIMixin],
  components: {
    CardToolbar,
    ExportBlockchain
  },
  data () {
    return {
      thename: null,
      info: null,
      keys: null,
      svg: null,
      iconcolor: null,
      blocked: null,
      ckst: '1',
      part1: '',
      part2: '',
      part3: '',
      kcvAes: '',
      cmacAes: '',
      materialFormatValue: BASE64_FORMAT_VALUE,
      itemIsAESor3DESorDES: false,
      keyType: null,
      keySize: null,
      keySubtype: null,
      itemFamily: null,
      itemIsAsymPub: false,
      // itemIsBlocked: false,
      needsImmediateExport: null,
      option: 'symmetric',
      firstOption: null,
      secondOption: null,
      password: null,
      repeatPassword: null,
      keyCheckValue: null,
      file: null,
      kek: null,
      kekType: null,
      kekSize: null,
      kekCertificate: null,
      // EMV CSR
      trackingNumber: null,
      serviceId: null,
      issuerId: null,
      certificateDate: null,
      crsType: null,
      modeOptions: [
        {
          name: 'CBC',
          value: MODE_CBC
        },
        {
          name: 'ECB',
          value: MODE_ECB
        }
      ],
      csrTypeOptions: [
        {
          name: 'Visa',
          value: 'Visa'
        },
        {
          name: 'Mastercard',
          value: 'Mastercard'
        },
        {
          name: 'Elo',
          value: 'Elo'
        },
        {
          name: 'Elo',
          value: 'Elo'
        }
      ],
      modeValue: MODE_CBC,
      paddingValue: null,
      jcbValue: null,
      ivValue: null,
      // P12
      certHavePK: null,
      pkcs12PrivateKey: null,
      pkcs12Certificate: null,
      currentStep: 0,
      map: {
        name: null,
        slot1: {
          value: null,
          search: null,
          searching: false
        },
        slot2: {
          value: null,
          search: null,
          searching: false
        }
      },
      isAES: false,
      page: 1,
      publicKeyExportAction: false,
      keyImportOption: 0,
      keyTypeId: null,
      kinfo: null,
      hasBchainAttribute: false
    }
  },
  created () {
    this.thename = this.$route.params.thename
    this.info = this.$route.params.info
    this.keys = this.$route.params.keys
    this.svg = this.$route.params.svg
    this.iconcolor = this.$route.params.iconcolor
    this.blocked = this.$route.params.blocked
    this.page = this.$route.params.page
    this.firstOption = this.$route.params.firstOption
    this.publicKeyExportAction = this.$route.params.firstOption !== undefined
    this.keyImportOption = this.$route.params.keyImportOption

    this.setKeyTypeAndSize(this.info.type)
    const hasBchainAttr = this.hasBchainAttr({ attr: this.info.attr })

    if (hasBchainAttr) {
      this.getBlockchainInfo()
    }
  },
  computed: {
    materialFormatHint () {
      switch (this.materialFormatValue) {
        case BASE64_FORMAT_VALUE:
          return this.$tc('base64_hint')

        case HEX_FORMAT_VALUE:
          return this.$tc('hex_hint')

        case BASE62_FORMAT_VALUE:
          return this.$tc('base62_hint')

        default:
          return this.$tc('base64_hint')
      }
    },
    showConversionButton () {
      return this.firstOption !== 'PKCS#8'
    },
    showExportButton () {
      return !this.needsImmediateExport && (this.firstOption !== 'Key Components' && this.firstOption !== 'Blockchain Export')
    },
    needsMode () {
      return this.isKEKSymmetric
    },
    needsPadding () {
      return !this.kekIsECC
    },
    needsIV () {
      // return true
      return this.modeValue === MODE_CBC && this.isKEKSymmetric
      // return this.isKEKSymmetric
    },
    inputPolicy () {
      return this.$store.state.secPolicy
    },
    // Fields Values
    outBlock1: {
      get () {
        return this.$store.state.export.keyComponents.outBlock1
      },
      set (val) {
        return this.$store.commit('export/SET_outBlock1', val)
      }
    },
    outBlock2: {
      get () {
        return this.$store.state.export.keyComponents.outBlock2
      },
      set (val) {
        return this.$store.commit('export/SET_outBlock2', val)
      }
    },
    outBlock3: {
      get () {
        return this.$store.state.export.keyComponents.outBlock3
      },
      set (val) {
        return this.$store.commit('export/SET_outBlock3', val)
      }
    },
    KCV1: {
      get () {
        return this.$store.state.export.keyComponents.kcv1
      },
      set (val) {
        return this.$store.commit('export/SET_kcv1', val)
      }
    },
    KCV2: {
      get () {
        return this.$store.state.export.keyComponents.kcv2
      },
      set (val) {
        return this.$store.commit('export/SET_kcv2', val)
      }
    },
    KCV3: {
      get () {
        return this.$store.state.export.keyComponents.kcv3
      },
      set (val) {
        return this.$store.commit('export/SET_kcv3', val)
      }
    },
    KCVfinal: {
      get () {
        return this.$store.state.export.keyComponents.kcvFinal
      },
      set (val) {
        return this.$store.commit('export/SET_kcvFinal', val)
      }
    },
    outBlock () {
      if (!this.rawOutBlock) {
        return null
      }
      if (this.firstOption === 'PKCS#8') {
        return this.rawOutBlock
      }
      if (this.materialFormatValue === BASE64_FORMAT_VALUE) {
        return toBase64(arrayBufferToString(this.rawOutBlock))
      } else if (this.materialFormatValue === HEX_FORMAT_VALUE) {
        return this.$store.getters['export/getHexDump']({
          buffer: this.rawOutBlock
        })
      } else {
        return base62.encodeStr(arrayBufferToString(this.rawOutBlock))
      }
    },
    rawOutBlock: {
      get () {
        return this.$store.state.export.rawOutBlock
      },
      set (val) {
        return this.$store.commit('export/SET_rawOutBlock', val)
      }
    },
    pubExponent () {
      return this.getPubExponent(BITMASK_RSA_PUB_EXP3, this.info.attr)
    },
    mapSlot1Search () {
      return this.map.slot1.search
    },
    mapSlot2Search () {
      return this.map.slot2.search
    },
    // LABELS
    buttonLabel () {
      if (this.firstOption === 'Key Components') {
        return this.$t('ok')
      }
      return this.$t('export')
    },
    // FLAGS
    isPKCS8or12 () {
      const requiresMoreOptions = ['PKCS#8', 'PKCS#12']
      return requiresMoreOptions.includes(this.firstOption)
    },
    kekIsECC () {
      const requiresMoreOptions = ['BrainPool', 'SecG Random', 'SecG Koblitz',
        'NIST Random', 'X9.62', 'Edwards', 'SECP']
      const val = requiresMoreOptions.includes(this.kekType)
      return val
    },
    isKEKSymmetric () {
      const val = this.kekType && this.kekType !== 'RSA' && this.kekType !== 'RSA PUB KEY' && this.kekType !== 'DSA' && !this.kekIsECC
      return val
    },
    outBlockReady () {
      return this.firstOption !== 'Key Components' && this.firstOption !== 'Blockchain Export'
    },
    kcvReady () {
      return (this.firstOption === 'Key Components' || this.isKEKSymmetric) && this.firstOption !== 'Key Components'
    },
    ivSize () {
      switch (this.kekType) {
        case 'AES': return (128 / 4)
        default: return (64 / 4)
      }
    },
    // KEYS
    paddingOptions () {
      if (this.kekType === 'RSA' || this.kekType === 'RSA PUB KEY') {
        return [
          {
            name: 'RSAES PKCS1 V1 5',
            value: KEK_WRAP_P1_PAD
          },
          {
            name: 'RSAES OAEP SHA 1',
            value: KEK_WRAP_OAEP_PAD
          },
          {
            name: 'RSAES OAEP SHA 256',
            value: KEK_WRAP_OAEP2_PAD
          }
        ]
      }
      return [
        {
          name: 'zero',
          value: KEK_WRAP_0_PAD
        },
        {
          name: 'PKCS#5',
          value: KEK_WRAP_P5_PAD
        }
      ]
    },
    keysFirstOptions () {
      function isPKCS1 (item) {
        return item.name === 'PKCS#1'
      }
      if (!this.itemFamily) return
      const allOptions = this.$store.getters['keys/getExportOptions']
      const options = allOptions[this.itemFamily].slice()

      const typesWithoutPKCS1 = ['BrainPool', 'X9.62', 'Edwards', 'SECP']
      const typesWithoutPKCS1andBlockchain = ['secp256k1', 'ed25519']
      const typesBlockchain = ['bip32xpriv', 'bip39sxpriv', 'bip32sxpriv', 'btcminikey', 'secp256k1', 'bip32ed25519xpriv']

      if (typesWithoutPKCS1.includes(this.keyType)) {
        // removing PKCS#1
        const pkcs1Index = options.findIndex(isPKCS1)
        options.splice(pkcs1Index, 1)
        if (typesWithoutPKCS1andBlockchain.includes(this.keyTypeId) && this.hasBchainAttribute) {
          options.push(
            {
              name: 'Blockchain Export',
              value: 'Blockchain Export'
            }
          )
        }
      } else if (typesBlockchain.includes(this.keyTypeId)) {
        return [
          {
            name: 'Clear Text Key',
            value: 'Clear Text Key'
          },
          {
            name: 'Blockchain Export',
            value: 'Blockchain Export'
          }
        ]
      } else if (this.itemFamily === 'symmetric' && this.itemIsAESor3DESorDES === false) {
        return [
          {
            name: 'With KEK',
            value: 'With KEK'
          },
          {
            name: 'Clear Text Key',
            value: 'Clear Text Key'
          }
        ]
      } else if (this.keyType && this.keyType === 'x.509') {
        const x509Options = [
          {
            name: 'Clear Text Key',
            value: 'Clear Text Key'
          }
        ]
        if (this.certHavePK) {
          x509Options.push(
            {
              name: 'PKCS#12',
              value: 'PKCS#12'
            }
          )
        }
        return x509Options
      }
      return options
    },
    keysSecondOptions () {
      if (this.keysFirstOptions) {
        const find = this.keysFirstOptions.find(
          item => item.name === this.firstOption
        )
        if (find && find.options) {
          return find.options
        }
      }
      return null
    },
    base64TooltipText () {
      switch (this.materialFormatValue) {
        case BASE64_FORMAT_VALUE:
          return this.$tc('hex_tooltip')

        case HEX_FORMAT_VALUE:
          return this.$tc('base62_tooltip')

        case BASE62_FORMAT_VALUE:
          return this.$tc('base64_tooltip')

        default:
          return this.$tc('hex_tooltip')
      }
    },
    // VALIDATIONS
    firstOptionErrors () {
      const errors = []
      if (!this.$v.firstOption.$dirty) return errors
      !this.$v.firstOption.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    secondOptionErrors () {
      const errors = []
      if (!this.$v.secondOption.$dirty) return errors
      !this.$v.secondOption.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    //    P12/P8
    passwordErrors () {
      const errors = []
      if (!this.$v.password.$dirty) return errors
      !this.$v.password.minLength &&
        errors.push(
          this.$t('$validation.min_length', {
            num: this.inputPolicy.min_len
          })
        )
      return errors
    },
    repeatPasswordErrors () {
      const errors = []
      if (!this.$v.repeatPassword.$dirty) return errors
      !this.$v.repeatPassword.sameAs &&
        errors.push(this.$t('$validation.same_as'))
      return errors
    },
    pkcs12CertificateErrors () {
      const errors = []
      if (!this.$v.pkcs12Certificate.$dirty) return errors
      !this.$v.pkcs12Certificate.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },

    //    KEK
    kekErrors () {
      const errors = []
      if (!this.$v.kek.$dirty) return errors
      !this.$v.kek.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    modeValueErrors () {
      const errors = []
      if (!this.$v.modeValue.$dirty) return errors
      !this.$v.modeValue.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    paddingValueErrors () {
      const errors = []
      if (!this.$v.paddingValue.$dirty) return errors
      !this.$v.paddingValue.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    ivValueErrors () {
      const errors = []
      if (!this.$v.ivValue.$dirty) return errors
      !this.$v.ivValue.required &&
        errors.push(this.$t('$validation.required'))
      !this.$v.ivValue.validHEXcharacter &&
        errors.push(this.$t('$validation.hex_not_valid'))
      !this.$v.ivValue.minLength &&
        errors.push(
          this.$t('$validation.min_length', {
            num: this.$v.ivValue.$params.minLength.min
          })
        )
      !this.$v.ivValue.maxLength &&
        errors.push(
          this.$t('$validation.max_length', {
            num: this.$v.ivValue.$params.maxLength.max
          })
        )
      return errors
    },
    // EMV CSR
    crsTypeErrors () {
      const errors = []
      if (!this.$v.crsType.$dirty) return errors
      !this.$v.crsType.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    trackingNumberErrors () {
      const errors = []
      if (!this.$v.trackingNumber.$dirty) return errors
      !this.$v.trackingNumber.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    serviceIdErrors () {
      const errors = []
      if (!this.$v.serviceId.$dirty) return errors
      !this.$v.serviceId.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    issuerIdErrors () {
      const errors = []
      if (!this.$v.issuerId.$dirty) return errors
      !this.$v.issuerId.required &&
        errors.push(this.$t('$validation.required'))
      !this.$v.issuerId.validHEXcharacter &&
        errors.push(this.$t('$validation.hex_not_valid'))
      return errors
    },
    certificateDateErrors () {
      const errors = []
      if (!this.$v.certificateDate.$dirty) return errors
      !this.$v.certificateDate.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    isBlockchainType () {
      const blockchainList = ['bip32xpriv', 'bip39sxpriv', 'bip32sxpriv', 'btcminikey']

      return blockchainList.includes(this.keyTypeId)
    }
  },
  validations () {
    const validation = {}

    const validHEXcharacter = value => {
      if (!value) return true
      const numbeOfValidChars = (value.match(/[0-9A-Fa-f]/g) || []).length
      return !(value.length - numbeOfValidChars > 0)
    }
    if (!this.needsImmediateExport) {
      validation.firstOption = {
        required
      }
    }
    if (this.keysSecondOptions) {
      validation.secondOption = {
        required
      }
    }
    // P12/P8
    if (this.firstOption === 'PKCS#12' || this.firstOption === 'PKCS#8') {
      validation.password = {
        minLength: minLength(this.inputPolicy.min_len)
      }
      validation.repeatPassword = {
        sameAs: sameAs('password')
      }
      if (this.keyType !== 'x.509' && this.firstOption === 'PKCS#12') {
        validation.pkcs12Certificate = {
          required
        }
      }
    }
    // KEK
    if (this.firstOption === 'With KEK') {
      validation.kek = {
        required
      }
      if (this.needsIV) {
        validation.ivValue = {
          required,
          validHEXcharacter,
          minLength: minLength(this.ivSize),
          maxLength: maxLength(this.ivSize)
        }
      }
      if (this.needsMode) {
        validation.modeValue = {
          required
        }
      }
      if (this.needsPadding) {
        validation.paddingValue = {
          required
        }
      }
    }

    // EMV CSR
    if (this.firstOption === 'EMV CSR') {
      validation.crsType = {
        required
      }
      validation.trackingNumber = {
        required
      }
      validation.serviceId = {
        required
      }
      validation.issuerId = {
        required,
        validHEXcharacter
      }
      validation.certificateDate = {
        required
      }
    }
    return validation
  },
  methods: {
    arrayBufferToBase62 (buffer) {
      const base62Characters = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
      let result = ''
      const bytes = new Uint8Array(buffer)
      for (let i = 0; i < bytes.length; i++) {
        result += base62Characters[bytes[i] % 62]
      }
      return result
    },
    changeMaterialFormat () {
      if (this.materialFormatValue < BASE62_FORMAT_VALUE) {
        this.materialFormatValue = this.materialFormatValue + 1
      } else {
        this.materialFormatValue = BASE64_FORMAT_VALUE
      }
    },
    versionInfo (version) {
      switch (version) {
        case XPRIV_TEST_NET:
        case HATHOR_TEST_NET:
          return this.$tc('test_net')

        case XPRIV_MAIN_NET:
        case HATHOR_MAIN_NET:
          return this.$tc('main_net')

        case FPRIV:
          return this.$tc('fpriv')

        case FPUB:
          return this.$tc('fpub')

        default:
          return version
      }
    },
    getBlockchainInfo () {
      return this.$store.dispatch('hsm/bchain_kinfo', {
        body: {
          key: this.thename
        }
      })
        .then((res) => {
          this.kinfo = res
          this.hasBchainAttribute = true
        })
        .catch(
          this.hasBchainAttribute = false
        )
    },
    aesExportKeyComponents () {
      const body = {
        ckst: parseInt(this.ckst),
        key: this.thename
      }

      this.$store.dispatch('export/expAesComponents', body).then(res => {
        this.outBlock1 = res.part1.replace(/0+$/, '')
        this.outBlock2 = res.part2.replace(/0+$/, '')
        this.outBlock3 = res.part3.replace(/0+$/, '')
        this.KCVfinal = res.cks

        if (this.ckst === '2') {
          this.KCV1 = this.getCMAC(res.part1.replace(/0+$/, ''))
          this.KCV2 = this.getCMAC(res.part2.replace(/0+$/, ''))
          this.KCV3 = this.getCMAC(res.part3.replace(/0+$/, ''))
        } else {
          this.KCV1 = this.getKCV(res.part1.replace(/0+$/, ''))
          this.KCV2 = this.getKCV(res.part2.replace(/0+$/, ''))
          this.KCV3 = this.getKCV(res.part3.replace(/0+$/, ''))
        }
      })
        .catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })

      this.currentStep = 1
    },
    getKCV (key) {
      const message = CryptoJS.enc.Hex.parse('00000000000000000000000000000000')
      const keyHex = CryptoJS.enc.Hex.parse(key)

      const kcv = CryptoJS.AES.encrypt(message, keyHex, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.NoPadding
      }).ciphertext.toString()

      return kcv.substr(0, 6).toUpperCase().replace(/0+$/, '')
    },
    getCMAC (key) {
      const message = '00000000000000000000000000000000'
      key = key.toString()

      const keyBuffer = Buffer.from(key, 'hex')
      const messageBuffer = Buffer.from(message, 'hex')
      const cmac = aesCmac(keyBuffer, messageBuffer, { returnAsBuffer: false })

      return cmac.slice(0, 6).toUpperCase()
    },
    exportKeyComponents () {
      this.currentStep = 1
      this.doExport()
    },
    setKeyTypeAndSize (integerType) {
      const algDetail = this.$store.getters['keys/getKeyType']({
        type: integerType
      })
      this.keyTypeId = algDetail.id
      this.keyType = algDetail.type
      this.keySize = algDetail.size
      this.keySubtype = algDetail.subtype
    },
    getPubExponent (bitmask, int) {
      if (int && bitmask === 1 ? this.$tc('yes') : this.$tc('no')) {
        return '2^16 + 1 (hexadecimal: 010001)'
      } else {
        return '3 (hexadecimal: 000011)'
      }
    },
    fileName () {
      if (this.info.type === ALG_OBJ_BLOB_X509) return this.thename + '.cer'
      switch (this.firstOption) {
        case 'PKCS#1':
          if (this.secondOption === 'Public Key') return 'pbk.txt'
          else if (this.secondOption === 'Public Key (X.509)') return 'pbk(x509).txt'
          else return this.thename + this.firstOption + '.bin'
        case 'PKCS#12':
          return this.thename + this.firstOption + '.pfx'
        case 'PKCS#8':
          return this.thename + this.firstOption + '.pem'
        default:
          return this.thename + this.firstOption + '.bin'
      }
    },
    saveToFile (res) {
      if (res) {
        const FileSaver = require('file-saver')
        let blob
        if (this.materialFormatValue === BASE64_FORMAT_VALUE || this.materialFormatValue === BASE62_FORMAT_VALUE) {
          blob = new Blob([res], { type: 'application/octet-stream' })
        } else {
          blob = new Blob([res], { type: 'text/plain;charset=utf-8' })
        }
        FileSaver.saveAs(blob, this.fileName())
      }
    },
    is3DESorDESorAES (aType) {
      const algDetail = this.$store.getters['keys/getKeyType']({
        type: aType
      })
      return algDetail.type === 'DES' || algDetail.type === '3DES' || algDetail.type === 'AES'
    },
    isImmediateExportNeeded (info) {
      const algDetail = this.$store.getters['keys/getKeyType']({
        type: info.type
      })
      const noOptionsNeeded = ['BLOB', 'PUB KEY', 'RSA PUB KEY', 'ECC PUB KEY', 'RSA SPKI', 'EDDSA PUB KEY', 'XECDH PUB KEY']
      const val = noOptionsNeeded.includes(algDetail.type)
      return val
    },
    isItemAsymPub (aType) {
      const algDetail = this.$store.getters['keys/getKeyType']({
        type: aType
      })
      const requiresMoreOptions = ['PUB KEY', 'RSA PUB KEY', 'ECC PUB KEY', 'RSA SPKI', 'EDDSA PUB KEY', 'XECDH PUB KEY']
      const val = requiresMoreOptions.includes(algDetail.type)
      return val
    },
    onFirstOptionSelected () {
      this.clearAllButFirstoption()
    },
    onSecondOptionSelected () {
      this.clearAllAfterSecondOption()
    },
    async onKEKSelected () {
      this.$v.kek.$touch()
      this.clearAllButKEKRelated()
      const info = await this.getSelectedKEKInfo()
      if (info) {
        this.kekType = info.type
        this.kekSize = info.size
      }
      if (this.kekType === 'RSA' || this.kekType === 'RSA PUB KEY' || this.kekIsECC) {
        this.modeValue = MODE_NULL
      } else {
        this.modeValue = MODE_CBC
      }
    },
    async onModeSelected () {
      this.$v.modeValue.$touch()
      this.ivValue = null
    },
    async getSelectedKEKInfo () {
      if (this.kek) {
        const info = await this.$store.dispatch('keys/getKeyInfo', {
          obj: this.kek
        })
        const infoDetail = this.$store.getters['keys/getKeyType']({
          type: info.type
        })
        return infoDetail
      }
      return null
    },
    closeModal () {
      this.clearAll()
      this.$router.push(
        {
          name: 'partition-keys',
          query: {
            p: this.page
          }
        }
      )
    },
    async findPrivateKeyForCert (certId) {
      const certP11Info = await this.$store.dispatch('keys/getP11Manager', {
        obj: certId
      })
      return certP11Info.HSM_ASSOCIATE
    },
    async keyExists (objId) {
      const keysList = await this.$store.dispatch('keys/getListKeys')
      const find = keysList.find(key => key === objId)
      const founded = find !== undefined
      return founded
    },
    async doExport () {
      try {
        if (this.firstOption === 'With KEK') {
          const body = {
            op: 1,
            mode: this.modeValue,
            pad: this.paddingValue || KEK_WRAP_NULL_PAD,
            type: 0,
            attr: 0,
            iv: this.ivValue || '12345678',
            kek: this.kek,
            key: this.thename
          }
          await this.$store.dispatch('export/kekWrap', body)
        } else if (this.itemFamily === 'symmetric') {
          if (this.firstOption === 'Clear Text Key') {
            await this.$store.dispatch('export/exportObj', { obj: this.thename })
          } else if (this.firstOption === 'Key Components') {
            const algDetail = await this.$store.getters['keys/getKeyType']({
              type: this.info.type
            })
            await this.$store.dispatch('export/expKeyComponents', { key: this.thename, size: algDetail.size, jcb: this.jcbValue })
          }
        } else if (this.itemFamily === 'asymmetric') {
          if (this.itemIsAsymPub) {
            if (this.info.type === ALG_OBJ_BLOB_X509) {
              await this.$store.dispatch('export/exportCert', { obj: this.thename })
            } else {
              await this.$store.dispatch('export/exportPubKey', { obj: this.thename })
              // await this.$store.dispatch('export/expAsymPub', { obj: this.thename, x509: 0 })
            }
          } else if (this.firstOption === 'PKCS#1') {
            if (this.secondOption === 'Private key') {
              await this.$store.dispatch('export/exportP1', { obj: this.thename })
            } else {
              const x509 = this.secondOption === 'Public key' ? 0 : 1
              await this.$store.dispatch('export/expAsymPub', { obj: this.thename, x509 })
            }
          } else if (this.firstOption === 'PKCS#8') {
            await this.$store.dispatch('export/expP8', { obj: this.thename, pwd: this.password })
          } else if (this.firstOption === 'PKCS#12') {
            await this.$store.dispatch('export/expP12', { key: this.thename, cert: this.pkcs12Certificate, pwd: this.password })
          } else if (this.firstOption === 'EMV CSR') {
            this.$notify.error({ message: this.$tc('not_implemented') })
          }
        } else {
          // None of the above
          if (this.keyType === 'x.509') {
            if (this.firstOption === 'Clear Text Key') {
              await this.$store.dispatch('export/exportObj', { obj: this.thename })
            } else if (this.firstOption === 'PKCS#12') {
              const privateKey = await this.findPrivateKeyForCert(this.thename)
              await this.$store.dispatch('export/expP12', { key: privateKey, cert: this.thename, pwd: this.password })
            }
          } else {
            // Others beside x.509
            await this.$store.dispatch('export/exportObj', { obj: this.thename })
          }
        }
      } catch (err) {
        const errObj = this.$store.getters['error/getErrorObj'](err)
        if (errObj) {
          this.$notify.hsmError(errObj, err)
        } else {
          this.$notify.error({ message: err })
        }
      }
    },

    clearAllButKEKRelated () {
      this.secondOption = null
      // this.outBlock = null
      this.clearAllAfterSecondOption()
    },

    clearAllButFirstoption () {
      this.clearAllButKEKRelated()
      this.kek = null
      this.kekType = null
      this.kekSize = null
      this.paddingValue = null
      this.modeValue = MODE_CBC
      this.ivValue = null
      this.map.slot1.value = null
      this.map.slot1.search = null
      this.map.slot1.searching = false
    },

    clearAllAfterSecondOption () {
      this.name = null
      this.exportable = false
      this.map.name = null
      this.map.slot2.value = null
      this.map.slot2.search = null
      this.map.slot2.searching = false
      this.file = null
      this.keyCheckValue = null
      // PKCS#12 & PKCS#8
      this.pkcs12Certificate = null
      this.itemIsAsymPub = null
      this.password = null
      this.repeatPassword = null
      // EMV CSR
      this.crsType = null
      this.trackingNumber = null
      this.serviceId = null
      this.issuerId = null
      this.certificateDate = null
      // KEY COMPONENTS
      this.currentStep = 0
      this.jcbValue = null
      // FROM STORE
      this.$store.commit('export/RESET')
      this.$v.$reset()
    },

    clearAll () {
      this.rawOutBlock = null
      this.ckst = '1'
      this.cks = ''
      this.part1 = ''
      this.part2 = ''
      this.part3 = ''
      this.firstOption = null
      this.certHavePK = null
      this.pkcs12PrivateKey = null
      this.materialFormatValue = BASE64_FORMAT_VALUE
      this.clearAllButFirstoption()
      this.$v.$reset()
    },
    itemIsAES (itemType) {
      const algDetail = this.$store.getters['keys/getKeyType']({
        type: itemType
      })
      return algDetail.type === 'AES'
    }
  },
  watch: {
    keysFirstOptions (options) {
      if (options.length === 1) {
        this.firstOption = options[0].value
      }
    },
    async info (val) {
      if (val) {
        this.needsImmediateExport = this.isImmediateExportNeeded(val)
        this.itemFamily = this.$store.getters['keys/getItemFamilyType']({ type: val.type })
        this.itemIsAESor3DESorDES = this.is3DESorDESorAES(val.type)
        this.isAES = this.itemIsAES(val.type)
        this.itemIsAsymPub = this.isItemAsymPub(val.type)
        this.setKeyTypeAndSize(val.type)
        if (this.keyType === 'x.509') {
          const privateKey = await this.findPrivateKeyForCert(this.thename)
          this.pkcs12PrivateKey = privateKey
          this.certHavePK = await this.keyExists(privateKey)
        }
      }
    },
    needsImmediateExport (val) {
      if (val) this.doExport()
    },
    mapSlot1Search (val) {
      if (val === '' || val === null) {
        this.map.slot1.value = null
      }
    },
    mapSlot2Search (val) {
      if (val === '' || val === null) {
        this.map.slot2.value = null
      }
    }
  }
}
</script>

<style scoped>
.lineDivider {
  width: 50%;
  margin: auto;
  margin-top: 5%;
  margin-bottom: 5%;
  border-color: black;
}

.step-content-indentation{
  margin-left: 9px;
}
</style>
